<template>
  <app-modal :modal-in="show" @close="$emit('close')" modal-class="modal-sm">
    <template #header>Refund — {{ formatAmount(transaction.transaction_amount) }}</template>

    <div class="p-1">

      <!-- RETURNED state -->
      <div v-if="state === 'returned'">
        <div class="mb-3 text-success fw-semibold">
          <i class="bi bi-check-circle me-2"></i>Marked as Returned
        </div>
        <button
          class="btn btn-outline-secondary btn-sm w-100"
          :disabled="!!processing"
          @click="handleUndoReturn"
        >
          <span v-if="processing === 'undo'" class="spinner-border spinner-border-sm me-1"></span>
          <span v-else><i class="bi bi-arrow-counterclockwise me-1"></i>Undo Return</span>
        </button>
        <p class="text-muted small mt-2 mb-0">
          <i class="bi bi-info-circle me-1"></i>COGS will be restored to this order.
        </p>
      </div>

      <!-- DISMISSED state -->
      <div v-else-if="state === 'dismissed'">
        <div class="mb-1 fw-semibold text-secondary">
          <i class="bi bi-eye-slash me-2"></i>Dismissed
        </div>
        <div v-if="transaction.transaction_memo" class="text-muted small mb-3 fst-italic">
          "{{ transaction.transaction_memo }}"
        </div>
        <button
          class="btn btn-outline-secondary btn-sm w-100"
          :disabled="!!processing"
          @click="handleUndoDismiss"
        >
          <span v-if="processing === 'undo'" class="spinner-border spinner-border-sm me-1"></span>
          <span v-else><i class="bi bi-arrow-counterclockwise me-1"></i>Undo — reclassify</span>
        </button>
      </div>

      <!-- UNASSIGNED state -->
      <div v-else>
        <p class="small text-muted mb-3">What was this refund for?</p>

        <button
          class="btn btn-outline-warning btn-sm w-100 mb-2"
          :disabled="!!processing"
          @click="handleMarkReturned"
        >
          <span v-if="processing === 'return'" class="spinner-border spinner-border-sm me-1"></span>
          <span v-else><i class="bi bi-arrow-return-left me-1"></i>Item was Returned</span>
        </button>

        <div v-if="!showDismiss">
          <button
            class="btn btn-outline-secondary btn-sm w-100"
            :disabled="!!processing"
            @click="showDismiss = true"
          >
            <i class="bi bi-eye-slash me-1"></i>Not a Return
          </button>
        </div>

        <div v-else>
          <input
            type="text"
            v-model="dismissReason"
            class="form-control form-control-sm mb-2"
            placeholder="Reason — e.g. shipping discount, goodwill refund"
            @keyup.enter="handleDismiss"
            @keyup.escape="showDismiss = false"
            ref="reasonInput"
          />
          <div class="d-flex gap-2">
            <button
              class="btn btn-outline-secondary btn-sm flex-grow-1"
              :disabled="!!processing"
              @click="handleDismiss"
            >
              <span v-if="processing === 'dismiss'" class="spinner-border spinner-border-sm me-1"></span>
              <span v-else>Apply</span>
            </button>
            <button class="btn btn-link btn-sm" @click="showDismiss = false">Cancel</button>
          </div>
        </div>
      </div>

    </div>
  </app-modal>
</template>

<script setup>
import { ref, computed, nextTick, watch, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import AppModal from '@/app/components/Utils/AppModal.vue'

const props = defineProps({
  show: Boolean,
  transaction: { type: Object, required: true },
  orderStatus: { type: String, required: true }
})

const emit = defineEmits(['close', 'updated'])

const store = useStore()
const processing = ref(null)
const showDismiss = ref(false)
const dismissReason = ref('')
const reasonInput = ref(null)

const state = computed(() => {
  if (props.orderStatus === 'Returned') return 'returned'
  if (props.transaction.ignored) return 'dismissed'
  return 'unassigned'
})

watch(showDismiss, (val) => {
  if (val) nextTick(() => reasonInput.value?.focus())
})

function formatAmount(amount) {
  return amount != null ? `$${Math.abs(parseFloat(amount)).toFixed(2)}` : ''
}

async function handleMarkReturned() {
  processing.value = 'return'
  try {
    await store.dispatch('eshop/markOrderReturned', { orderId: props.transaction.order_id })
    emit('updated')
    emit('close')
  } catch (e) {
    alert(e.message || 'Failed to mark as returned')
  }
  processing.value = null
}

async function handleDismiss() {
  processing.value = 'dismiss'
  try {
    await store.dispatch('eshop/ignoreTransaction', { id: props.transaction.id, reason: dismissReason.value })
    emit('updated')
    emit('close')
  } catch (e) {
    alert(e.message || 'Failed to dismiss')
  }
  processing.value = null
}

async function handleUndoReturn() {
  processing.value = 'undo'
  try {
    await store.dispatch('eshop/undoReturn', { orderId: props.transaction.order_id })
    emit('updated')
    emit('close')
  } catch (e) {
    alert(e.message || 'Failed to undo return')
  }
  processing.value = null
}

async function handleUndoDismiss() {
  processing.value = 'undo'
  try {
    await store.dispatch('eshop/undoDismissedRefund', { id: props.transaction.id })
    emit('updated')
    emit('close')
  } catch (e) {
    alert(e.message || 'Failed to undo')
  }
  processing.value = null
}
</script>
